import React, { useState, useEffect } from 'react';
import axiosInstance from '../../services/axios-interceptor';
import moment from 'moment';
import { Link } from 'react-router-dom';
const API_URL = `${process.env.REACT_APP_API_BASE_URL}`;

interface StationIssue {
    stationId: string;
    flagType: string;
    startDate: string;
}

interface StationIssuesData {
    issues: StationIssue[];
}

interface FlagGroupConfig {
    title: string;
    slug: string;
    flags: string[];
    icon: string;
    color: string;
}

const FLAG_GROUPS: FlagGroupConfig[] = [
    {
        title: 'Precipitation Issues',
        slug: 'precipitation',
        flags: ['tahmo_qc_precip',
            'rainfall_bad_satellite',
            'Partial Clog',     // Updated to match exact flag name
            'Low Rainfall',     // Updated to match exact flag name
            'Dripping Clog',
            'Precipitation QC',
            'TAHMO QC Precipitation'
        ],
        icon: 'fa-tint',
        color: 'primary',
    },
    {
        title: 'Hardware Issues',
        slug: 'battery',
        flags: [
            'Battery may fail soon',
            'Rapid Battery Drain',
            'Severe Battery Issue',
            'Offline (battery)',
            'Offline (other reason)',
            'TAHMO QC Battery Level',
        'Low Battery Capacity'],
        icon: 'fa-microchip',
        color: 'danger'
    },
    {
        title: 'Other Sensor Issues',
         slug: 'sensor',
        flags: ['tahmo_qc_htp', 'TAHMO QC Solar', 'TAHMO QC Wind Gust',
            'TAHMO QC Wind Speed',
            'TAHMO QC Wind Direction',
            'Solar Radiation QC',
            'Wind QC',
            'TAHMO QC Temperature',
            'TAHMO QC Soil Electrical Conductivity',
            'TAHMO QC Soil Temperature',
            'TAHMO QC Lightning Distance',
            'TAHMO QC Humidity',
            'TAHMO QC Tilt X',
            'TAHMO QC Tilt Y',
            'TAHMO QC Pressure',
            'TAHMO QC Humidity/Temp',
            'TAHMO QC Lightning Events',
            'TAHMO QC Soil Moisture',
            'TAHMO QC Logger temperature',
            'TAHMO QC Logger reference pressure',
            'TAHMO QC Vapor Pressure',
            'TAHMO QC Humidity/Temp/Pressure',
            'TAHMO QC Tilt angle',
            'Humidity/Temp/Pressure QC'],
        icon: 'fa-thermometer-half',
        color: 'warning'
    },
];

interface GroupedStationIssue {
    station_id: string;
    flags: {
        flag_type: string;
        start_date: string;
    }[];
}

const StationIssuesCard: React.FC = () => {
    const [data, setData] = useState<StationIssuesData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axiosInstance.get(API_URL + 'dashboard/station_issues/');
                setData(response.data);
            } catch (err) {
                setError('Failed to fetch station issues');
                console.error('Error fetching station issues:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const formatDate = (dateString: string) => {
        return moment(dateString).format('YYYY-MM-DD HH:mm:ss');
    };

   const groupIssuesByStationAndConfig = (issues: StationIssue[]) => {
        // First, group issues by flag group
        const groupedByCategory = FLAG_GROUPS.map(group => {
            // For each flag group, get all relevant issues
            const stationGroups: Record<string, GroupedStationIssue> = {};

             issues.forEach(issue => {
                // Check if this issue's flag belongs to current group
                if (group.flags.includes(issue.flagType)) {
                    if (!stationGroups[issue.stationId]) {
                        stationGroups[issue.stationId] = {
                            station_id: issue.stationId,
                            flags: []
                        };
                    }
                    stationGroups[issue.stationId].flags.push({
                        flag_type: issue.flagType,
                        start_date: issue.startDate
                    });
                }
            });


            return {
                ...group,
                stations: Object.values(stationGroups)
            };
        });

        // Handle uncategorized issues
        const allConfiguredFlags = FLAG_GROUPS.flatMap(group => group.flags);
        const uncategorizedIssues = issues.filter(issue =>
            !allConfiguredFlags.includes(issue.flagType)
        );

        if (uncategorizedIssues.length > 0) {
            const uncategorizedStationGroups: Record<string, GroupedStationIssue> = {};

            uncategorizedIssues.forEach(issue => {
            if (!uncategorizedStationGroups[issue.stationId]) {
                uncategorizedStationGroups[issue.stationId] = {
                    station_id: issue.stationId,
                    flags: []
                };
            }
            uncategorizedStationGroups[issue.stationId].flags.push({
                flag_type: issue.flagType,
                start_date: issue.startDate
            });
        });


            groupedByCategory.push({
                title: 'Other Issues',
                slug: 'other',
                flags: [],
                icon: 'fa-exclamation-circle',
                color: 'secondary',
                stations: Object.values(uncategorizedStationGroups)
            });
        }

        return groupedByCategory;
    };


    if (loading) {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="text-center">Loading station issues...</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="alert alert-danger">{error}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!data || !data.issues) {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="alert alert-warning">No issues available</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const groupedIssues = groupIssuesByStationAndConfig(data.issues);

    return (
        <div className="row">
            {groupedIssues.map((group) => (
                <div key={group.title} className="col-md-6 col-xl-4">
                    <div className={`card bg-${group.color}`}>
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <i className={`fa ${group.icon} fa-2x text-white`}></i>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h4 className="mt-0 mb-1 text-white">{group.title}</h4>
                                    <p className="mb-0 text-white-50">
                                        {group.stations.length} stations affected
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="card-body bg-white">
                            <div className="table-responsive" style={{maxHeight: '250px', overflowY: 'auto'}}>
                                <table className="table table-sm table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th>Station ID</th>
                                            <th>Flags</th>
                                            <th>Flag Start</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {group.stations.map((station) => (
                                            <tr key={station.station_id}>
                                                <td>
                                                    <Link to={`/stations/${station.station_id}/timeseries/${encodeURIComponent(group.slug)}?start_date=${station.flags[0].start_date}&end_date=${moment().format('YYYY-MM-DDTHH:mm:ss')}`}>
                                                        {station.station_id}
                                                    </Link>
                                                </td>
                                                <td>
                                                    <Link to={`/stations/${station.station_id}/timeseries/${encodeURIComponent(group.slug)}?start_date=${station.flags[0].start_date}&end_date=${moment().format('YYYY-MM-DDTHH:mm:ss')}`}>
                                                    {station.flags.map(flag => flag.flag_type).join(', ')}
                                                    </Link>
                                                </td>
                                                <td>
                                                    <Link to={`/stations/${station.station_id}/timeseries/${encodeURIComponent(group.slug)}?start_date=${station.flags[0].start_date}&end_date=${moment().format('YYYY-MM-DDTHH:mm:ss')}`}>
                                                    {formatDate(station.flags[0].start_date)}
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

};

export default StationIssuesCard;
